import { SHIPPING_CONFIG } from "@/config/shipping";

/**
 * Pure free-shipping math, deliberately split out of free-shipping-progress.tsx (Task 14
 * review round 2, critical fix).
 *
 * free-shipping-progress.tsx starts with "use client" (it exports a React component that
 * uses next-intl hooks). Next's RSC compiler replaces EVERY export of a "use client" module
 * -- not just the component -- with a registerClientReference proxy when that module is
 * pulled into the server module graph; calling one of those proxies as a plain function
 * throws ("Attempted to call ... from the server") at request time. src/app/[locale]/
 * [channel]/(main)/cart/page.tsx is a Server Component that needs resolveFreeShippingProgress
 * as a plain function call, not as JSX -- so it must import from a directive-free module.
 *
 * This file has NO "use client" and imports nothing client-only, so it is safe to import
 * from both Server Components (cart/page.tsx) and Client Components (cart-drawer.tsx,
 * cart-upsell.tsx, FreeShippingProgress below). free-shipping-progress.tsx re-exports these
 * two functions so existing checkout imports keep working unchanged.
 */

/**
 * The threshold is NOT a literal here: src/config/shipping.ts is the mirror of the Saleor
 * shipping method that actually grants the free rate (maxOrder = threshold - 0.01, see the
 * shipping-pricing notes). Hardcoding 250 in a component is how the promise and the rule
 * get out of sync.
 */
export function resolveFreeShippingThreshold(channel: string): number {
	return (
		SHIPPING_CONFIG.freeShippingThreshold[channel as keyof typeof SHIPPING_CONFIG.freeShippingThreshold] ??
		SHIPPING_CONFIG.freeShippingThreshold.eu
	);
}

export interface FreeShippingProgressState {
	/** Threshold in the CHANNEL SETTLEMENT currency (eu -> EUR, us -> USD). */
	threshold: number;
	/** How much is still missing; 0 once the threshold is reached. */
	remaining: number;
	/** 0..100, clamped. */
	percent: number;
	qualified: boolean;
}

/**
 * Pure progress math. `subtotal` is the merchandise subtotal in the same currency as the
 * threshold -- never a reference-currency amount, or the meter would compare HUF against 250 EUR.
 */
export function resolveFreeShippingProgress(subtotal: number, channel: string): FreeShippingProgressState {
	const threshold = resolveFreeShippingThreshold(channel);
	const safeSubtotal = Number.isFinite(subtotal) && subtotal > 0 ? subtotal : 0;
	const remaining = Math.max(threshold - safeSubtotal, 0);
	const percent = threshold > 0 ? Math.min((safeSubtotal / threshold) * 100, 100) : 100;

	return { threshold, remaining, percent, qualified: remaining <= 0 };
}

/**
 * The flat-rate shipping estimate for a channel, in that channel's settlement currency.
 *
 * Same reasoning as resolveFreeShippingThreshold above: the rate lives in
 * src/config/shipping.ts, and every surface that shows it (mini-cart, /cart, the PDP buy
 * box) has to read it from there rather than repeat `SHIPPING_CONFIG.flatRate[channel] ??
 * .eu` inline. Three hand-copied lookups is how the cart and the PDP end up quoting
 * different shipping costs for the same order.
 *
 * It is an ESTIMATE by construction: no delivery address exists on a PDP or in a cart, so
 * Saleor cannot price a real method yet. Callers must label it as such.
 */
export function resolveShippingFlatRate(channel: string): number {
	return (
		SHIPPING_CONFIG.flatRate[channel as keyof typeof SHIPPING_CONFIG.flatRate] ?? SHIPPING_CONFIG.flatRate.eu
	);
}
